import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Withdraw.css';
import axios from 'axios';

function Withdraw() {
  const navigate = useNavigate();
  const [method, setMethod] = useState(null);
  // const [upiId, setUpiId] = useState('');
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardHolder: '',
    ifscCode: ''
  });
  const [activeAccountIndex, setActiveAccountIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const cardsContainerRef = useRef(null);
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = userData.userId || userData._id;

  // Fetch saved accounts from backend
  const fetchSavedAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/bank-accounts/${userId}`);
      
      if (response.data.success) {
        setSavedAccounts(response.data.accounts);
      } else {
        // Fallback to localStorage if API fails
        const saved = localStorage.getItem('savedBankAccounts');
        if (saved) {
          setSavedAccounts(JSON.parse(saved));
        }
      }
    } catch (error) {
      console.error('Error fetching saved accounts:', error);
      // Fallback to localStorage if API fails
      const saved = localStorage.getItem('savedBankAccounts');
      if (saved) {
        setSavedAccounts(JSON.parse(saved));
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Load saved accounts from API on component mount
  useEffect(() => {
    fetchSavedAccounts();
  }, [fetchSavedAccounts]);

  const handleProceedWithdraw = (methodType, details) => {
    if (methodType === 'upi' && !details.upiId) {
      alert('Please enter a valid UPI ID');
      return;
    }
    
    if (methodType === 'card' && (!details.cardNumber || !details.cardHolder || !details.ifscCode)) {
      alert('Please enter complete bank details');
      return;
    }
    
    navigate('/withdraw-amount', { state: { method: methodType, details } });
  };

  const handleSaveAccount = async () => {
    if (!cardDetails.cardNumber || !cardDetails.cardHolder || !cardDetails.ifscCode) {
      alert('Please enter complete bank details to save.');
      return;
    }

    try {
      setLoading(true);
      // Send data to backend
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/bank-accounts/save`, {
        userId,
        ...cardDetails
      });
      
      if (response.data.success) {
        // Update local state with the data from the server (includes ID)
        const savedAccount = response.data.account;
        const updatedAccounts = [...savedAccounts, savedAccount];
        setSavedAccounts(updatedAccounts);
        
        // Also update localStorage as fallback
        localStorage.setItem('savedBankAccounts', JSON.stringify(updatedAccounts));
        
        alert('Bank account details saved successfully!');
        
        // Clear the form
        setCardDetails({
          cardNumber: '',
          cardHolder: '',
          ifscCode: ''
        });
      } else {
        alert('Failed to save account: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving account:', error);
      
      if (error.response && error.response.data && error.response.data.message) {
        alert(error.response.data.message);
      } else {
        // Fallback to localStorage if API fails
        const newAccount = {
          id: Date.now(),
          ...cardDetails
        };
        
        const updatedAccounts = [...savedAccounts, newAccount];
        setSavedAccounts(updatedAccounts);
        localStorage.setItem('savedBankAccounts', JSON.stringify(updatedAccounts));
        
        alert('Bank account details saved locally (offline mode)');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSavedAccount = (account) => {
    setCardDetails(account);
    setMethod('card');
  };

  const handleDeleteSavedAccount = async (id) => {
    try {
      setLoading(true);
      const response = await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/bank-accounts/${id}`);
      
      if (response.data.success) {
        const updatedAccounts = savedAccounts.filter(account => account.id !== id);
        setSavedAccounts(updatedAccounts);
        localStorage.setItem('savedBankAccounts', JSON.stringify(updatedAccounts));
        alert('Account deleted successfully');
      } else {
        alert('Failed to delete account: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      
      // Fallback to local deletion if API fails
      const updatedAccounts = savedAccounts.filter(account => account.id !== id);
      setSavedAccounts(updatedAccounts);
      localStorage.setItem('savedBankAccounts', JSON.stringify(updatedAccounts));
      alert('Account deleted locally (offline mode)');
    } finally {
      setLoading(false);
    }
  };

  const handleCardNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 16) value = value.slice(0, 16);
    value = value.replace(/(\d{4})/g, '$1 ').trim();
    setCardDetails({ ...cardDetails, cardNumber: value });
  };

  const handleIfscChange = (e) => {
    let value = e.target.value.toUpperCase();
    if (value.length > 11) value = value.slice(0, 11);
    setCardDetails({ ...cardDetails, ifscCode: value });
  };

  const nextAccount = () => {
    if (activeAccountIndex < savedAccounts.length - 1) {
      setActiveAccountIndex(prev => prev + 1);
      scrollToCard(activeAccountIndex + 1);
    }
  };

  const previousAccount = () => {
    if (activeAccountIndex > 0) {
      setActiveAccountIndex(prev => prev - 1);
      scrollToCard(activeAccountIndex - 1);
    }
  };

  const scrollToCard = (index) => {
    const container = cardsContainerRef.current;
    if (container) {
      const cardWidth = container.offsetWidth;
      container.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="withdraw-container">
      <div className="withdraw-header">
        <button className="back-button" onClick={() => navigate('/wallet')}>← </button>
        <h2>Withdraw Funds</h2>
      </div>

      {loading && (
        <div className="loading-indicator">
          <div className="loader"></div>
          <p>Loading...</p>
        </div>
      )}

      {savedAccounts.length > 0 && (
        <div className="saved-accounts-section">
          <h3>Saved Bank Accounts</h3>
          <div className="saved-accounts-wrapper">
            <button 
              className="scroll-button left"
              onClick={previousAccount}
              style={{ visibility: activeAccountIndex === 0 ? 'hidden' : 'visible' }}
            >
              ‹
            </button>
            <div className="saved-accounts-list" ref={cardsContainerRef}>
              {savedAccounts.map((account, index) => (
                <div 
                  key={account.id} 
                  className={`saved-account-card ${index === activeAccountIndex ? 'active' : ''}`}
                >
                  <div 
                    className="saved-card-preview"
                    onClick={() => handleSelectSavedAccount(account)}
                  >
                    <div className="card-front saved">
                      <div className="card-type">Saved Account</div>
                      <div className="card-number">
                        {account.cardNumber.replace(/\d(?=\d{4})/g, "•")}
                      </div>
                      <div className="card-info">
                        <div className="card-holder">
                          <span>Account Holder</span>
                          <div>{account.cardHolder}</div>
                        </div>
                        <div className="ifsc-code">
                          <span>IFSC Code</span>
                          <div>{account.ifscCode}</div>
                        </div>
                      </div>
                    </div>
                    <button 
                      className="delete-account-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSavedAccount(account.id);
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button 
              className="scroll-button right"
              onClick={nextAccount}
              style={{ visibility: activeAccountIndex === savedAccounts.length - 1 ? 'hidden' : 'visible' }}
            >
              ›
            </button>
          </div>
        </div>
      )}

      <div className="withdraw-methods">
        {/* <div
          className={`withdraw-card ${method === 'upi' ? 'active' : ''}`}
          onClick={() => setMethod('upi')}
        >
          <h3>Withdraw via UPI</h3>
          <p>Instant transfer to your UPI account.</p>
          {method === 'upi' && (
            <div className="input-group">
              <label htmlFor="upiId">Enter UPI ID</label>
              <input
                type="text"
                id="upiId"
                placeholder="example@upi"
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
              />
              <button 
                className="submit-btn" 
                onClick={() => handleProceedWithdraw('upi', { upiId })}
                disabled={!upiId || loading}
              >
                Proceed to Withdraw
              </button>
            </div>
          )}
        </div> */}

        <div
          className={`withdraw-card ${method === 'card' ? 'active' : ''}`}
          onClick={() => setMethod('card')}
        >
          <h3>Withdraw via Bank Transfer</h3>
          <p>2-3 business days to your bank account.</p>
          {method === 'card' && (
            <div className="debit-card-form">
              <div className="card-preview">
                <div className="card-front">
                  <div className="card-type">Bank Account</div>
                  <div className="card-number">{cardDetails.cardNumber || '•••• •••• •••• ••••'}</div>
                  <div className="card-info">
                    <div className="card-holder">
                      <span>Account Holder</span>
                      <div>{cardDetails.cardHolder || 'YOUR NAME'}</div>
                    </div>
                    <div className="ifsc-code">
                      <span>IFSC Code</span>
                      <div>{cardDetails.ifscCode || 'XXXX0000XXX'}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="cardNumber">Account Number</label>
                <input
                  type="text"
                  id="cardNumber"
                  placeholder="Enter your account number"
                  value={cardDetails.cardNumber}
                  onChange={handleCardNumberChange}
                  maxLength="19"
                />

                <label htmlFor="cardHolder">Account Holder Name</label>
                <input
                  type="text"
                  id="cardHolder"
                  placeholder="Full Name"
                  value={cardDetails.cardHolder}
                  onChange={(e) => setCardDetails({ ...cardDetails, cardHolder: e.target.value.toUpperCase() })}
                />

                <label htmlFor="ifscCode">IFSC Code</label>
                <input
                  type="text"
                  id="ifscCode"
                  placeholder="BANK0123456"
                  value={cardDetails.ifscCode}
                  onChange={handleIfscChange}
                  maxLength="11"
                />

                <div className="button-group">
                  <button 
                    className="save-btn" 
                    onClick={handleSaveAccount}
                    disabled={loading}
                  >
                    Save Account
                  </button>
                  <button 
                    className="submit-btn" 
                    onClick={() => handleProceedWithdraw('card', cardDetails)}
                    disabled={!cardDetails.cardNumber || !cardDetails.cardHolder || !cardDetails.ifscCode || loading}
                  >
                    Proceed to Withdraw
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Withdraw;
